import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, CanActivate, CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FlashMessageService } from '../../shared/components/flash-message/flash-message.service';

export interface ComponentCanDeactivate {
  canDeactivate: () => boolean | Observable<boolean>;
}


@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanDeactivate<ComponentCanDeactivate> {
  isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly router: Router,
    private readonly authenticationService: AuthenticationService,
    private readonly translate: TranslateService,
    private readonly flashMessage: FlashMessageService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (!this.isBrowser) {
      return true;
    }

    if (state.url.startsWith('/auth/login')) {
      return true;
    }

    if (state.url.startsWith('/manufacturer')) {
      return true;
    }

    const currentUser = this.authenticationService.currentUserValue;

    if (!currentUser?.Token) {
      this.router.navigate(['/home']);
      return false;
    }

    if (!this.authenticationService.isTokenValid) {

      const isManufacturerPage = state.url.toLowerCase().startsWith('/manufacturer');
      this.authenticationService.logout();

      if (isManufacturerPage) {
        this.flashMessage.showErrorAlert(this.translate.instant("session_expired"));
        return false;
      }

      this.router.navigate(['/home']);
      return false;
    }

    const allowedRoles = route.data['roles'] as number[] | undefined;

    if (allowedRoles && allowedRoles.length > 0) {

      const hasRequiredRole = allowedRoles.includes(currentUser.RoleId);

      if (!hasRequiredRole) {
        this.router.navigate(['/home']);
        return false;
      }
    }

    return true;
  }


  canDeactivate(component: ComponentCanDeactivate): Observable<boolean> {

    const canLeave = component.canDeactivate();

    if (typeof canLeave === 'boolean') {

      if (canLeave) {
        return of(true);
      }

      return this.translate.get('unsaved_warning').pipe(
        switchMap(
          (msg: string) =>
            of(confirm(msg))
        )
      );
    }

    return canLeave;
  }
}
